<script setup>
import { onMounted, onUpdated, reactive, ref } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import {
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { MenuAlt2Icon, XIcon, UserCircleIcon } from '@heroicons/vue/outline'
import AdminMenu from '@common/AdminMenu'
import { Inertia } from '@inertiajs/inertia'
import { useStore } from '@admin_js/store'
import Notifications from '@common/Notifications'
import Flash from '@common/Elements/Flash'


const store = useStore()
const sidebarOpen = ref(false)
const userNavigation = reactive([
  {
    id: 1,
    label: 'Log Uit',
    method: 'delete',
    as: 'button',
    route: 'admin.sessions.logout',
  },
])

const getRoute = (item) => {
  return route(item.route)
}

onMounted(() => {
  store.setNotifications(Inertia.page.props.notifications)
  store.setUser(Inertia.page.props.user)
})
</script>
<template>
  <div class="min-h-full">
    <TransitionRoot as="template" :show="sidebarOpen" class="min-h-full">
      <Dialog
        as="div"
        class="fixed inset-0 z-40 flex md:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />

        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div
            class="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4"
          >
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  class="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="flex flex-shrink-0 items-center px-4">
              <img
                class="h-8 w-auto"
                src="https://digitalhq.com/images/logo/digitalhq.png"
                alt="Digital HQ"
              />
            </div>
            <div class="mt-5 h-0 flex-1 overflow-y-auto">
                <AdminMenu></AdminMenu>
            </div>
          </div>
        </TransitionChild>
        <div class="w-14 flex-shrink-0" aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div
        class="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5"
      >
        <div class="flex flex-shrink-0 items-center px-4">
          <img
            class="w-auto"
            src="https://digitalhq.com/images/logo/digitalhq.png"
            alt="Digital HQ"
          />
        </div>
        <div class="mt-5 flex flex-grow flex-col" id="nav">
          <nav class="flex-1 space-y-1 px-2 pb-4">
            <AdminMenu></AdminMenu>
          </nav>
        </div>
      </div>
    </div>
    <div class="flex min-h-full flex-1 flex-col md:pl-64">
      <div class="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
        <button
          type="button"
          class="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-500 md:hidden"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
        </button>
        <div class="flex flex-1 justify-end px-4">
          <div class="ml-4 flex items-center md:ml-6">
            <div id="header"></div>
            <button
              type="button"
              class="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <span class="sr-only">View notifications</span>
              <Notifications />
            </button>

            <!-- Profile dropdown -->
            <Menu as="div" class="relative ml-3">
              <div>
                <MenuButton
                  class="flex max-w-xs items-center rounded-full bg-white text-sm text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  <span class="sr-only">Open user menu</span>
                  <UserCircleIcon class="h-6 w-6 rounded-full" />
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="opacity-0 transform scale-95"
                enter-to-class="opacity-100 transform scale-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="opacity-100 transform scale-100"
                leave-to-class="opacity-0 transform scale-95"
              >
                <MenuItems
                  class="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <MenuItem
                    v-for="item in userNavigation"
                    :key="item.name"
                    v-slot="{ active }"
                  >
                    <Link
                      :method="item.method"
                      :as="item.as"
                      :href="getRoute(item)"
                      :class="[
                        active ? 'bg-gray-100' : '',
                        'block w-full px-4 py-2 text-left text-sm text-gray-700',
                      ]"
                      >{{ item.label }}
                    </Link>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <main class="min-h-full flex-1">
        <div class="min-h-full py-6">
          <div class="mx-auto min-h-full px-4 sm:px-6 md:px-8">
            <Flash :flash="Inertia.page.props.flash" />
            <slot />
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
